import { useCallback, useEffect, useState } from "react";
import apiClient from "../../../../../helpers/api-client";
import { triggerFileDownload } from "../../../../../helpers/download";
import i18n from "../../../../../i18n";
import AudioTranscriptPDFGenerator, { AudioTranscriptReport } from "../../../AudioTranscriptPDFGenerator";
import { formatEventTimestamp } from "../../../ProctoringResultsPage/ProctoringEventsTable";
import { VideoEvent } from "../../../../utilities/useTimestamps";

/**
 * Shared by TimestampLinks and EventsDownloadMenu (and PDFGenerator, for report rendering) — pulled out of
 * TimestampLinks so those two don't import from each other (TimestampLinks rendering EventsDownloadMenu,
 * EventsDownloadMenu importing these helpers back from TimestampLinks), which is a circular dependency
 * that can produce undefined exports depending on ESM evaluation order.
 */
export const processKeypressData = (data: string | string[]) => {
	// [
	//	{
	//	  "time": "0:00",
	//	  "value": "[Shift]Hi [Shift]Test 123"
	//	},
	//	{
	//	  "time": "0:58",
	//	  "value": "[Shift]Hi [Shift]Check 123"
	//	}
	// ]
	try {
		if (typeof data === "string") {
			const parsed = JSON.parse(data) as string[];
			return parsed.join("").toString();
		} else {
			return data
				.map((event) => {
					let result = "";
					if (event.length > 1) result += "[" + event + "]";
					else result += event;
					return result;
				})
				.join("");
		}
	} catch {
		return data;
	}
};

export const processKeypressTime = (eventTime: number, offsetTime: number) => {
	if (eventTime * 1000 < offsetTime) {
		return 0;
	} else {
		return eventTime * 1000 - offsetTime;
	}
};

export const useAudioTranscriptFromDataDump = (proctoringSessionUuid?: string) => {
	const [transcript, setTranscript] = useState<string[]>([]);

	useEffect(() => {
		async function fetchData() {
			if (!proctoringSessionUuid) return;
			const { links } = await apiClient.dataDump.getLinks({ pathParams: { proctoringSessionUuid } });
			const { transcriptLinks } = {
				transcriptLinks: links.filter((link) => link.source === "machine-learning-audio-transcription"),
			};
			const transcripts = (await Promise.all(
				transcriptLinks.map((link) => fetch(link.downloadUrl).then((response) => (response.ok ? response.json() : []))),
			)) as string[];
			setTranscript(transcripts.flat());
		}

		void fetchData();
	}, [proctoringSessionUuid]);

	return [transcript];
};

type UseEventDownloadsParams = {
	events: VideoEvent[];
	offsetGetter: number;
	duration: number;
	assessmentName: string;
	studentName: string;
	sessionId: string;
	courseName?: string;
	attemptNumber?: number;
	assessmentStatus?: string;
	recordingDate?: string;
	/**
	 * Pass this when the caller already fetched transcripts (TimestampLinks does, so that the menu it
	 * renders and the one a caller hands it don't each fetch for the same session) — skips this hook's
	 * own data-dump fetch.
	 */
	transcripts?: string[];
};

/**
 * Availability/handlers for the keystrokes, application-activity, and audio-transcript downloads behind
 * EventsDownloadMenu's kebab items, keeping that logic — and the audio-transcript data-dump fetch in
 * particular — in one place.
 */
export function useEventDownloads({
	events,
	offsetGetter,
	duration,
	assessmentName,
	studentName,
	sessionId,
	courseName,
	attemptNumber,
	assessmentStatus,
	recordingDate,
	transcripts: precomputedTranscripts,
}: UseEventDownloadsParams) {
	const [fetchedTranscripts] = useAudioTranscriptFromDataDump(precomputedTranscripts ? undefined : sessionId);
	const transcripts = precomputedTranscripts ?? fetchedTranscripts;
	const [showAudioTranscriptPDF, setShowAudioTranscriptPDF] = useState(false);

	const isDownloadKeystrokesAvailable = events.some((event) => event.type === "keyboard" && event.name === "keypress");
	const isDownloadAppActivityAvailable = events.some(
		(event) => event.type === "system" && event.name === "applications activity",
	);
	const isDownloadAudioTranscriptAvailable = transcripts?.length > 0;

	const handleDownloadKeystrokes = useCallback(() => {
		const data = events
			.filter((event) => event.type === "keyboard" && event.name === "keypress")
			.map((event) => {
				const time = processKeypressTime(event.time, offsetGetter);
				return {
					time: formatEventTimestamp(time, duration),
					value: processKeypressData(event.details.value),
				};
			});
		const dataStr = JSON.stringify({ [i18n.t("result.components.resultsPage.downloadKeystrokes")]: data }, null, 2);
		const blob = new Blob([dataStr], { type: "text/plain" });
		triggerFileDownload(blob, `${assessmentName}_${studentName}_Keystrokes.txt`);
	}, [events, offsetGetter, duration, assessmentName, studentName]);

	const handleDownloadApplicationActivity = useCallback(() => {
		const data = events
			.filter((event) => event.type === "system" && event.name === "applications activity")
			.map((event) => {
				const time = processKeypressTime(event.time, offsetGetter);
				return {
					time: formatEventTimestamp(time, duration),
					appName: event.details.appName as string,
					action: event.details.activity as string,
				};
			});
		const dataStr = JSON.stringify({ [i18n.t("result.components.resultsPage.downloadAppsActivity")]: data }, null, 2);
		const blob = new Blob([dataStr], { type: "text/plain" });
		triggerFileDownload(blob, `${assessmentName}_${studentName}_AppsActivity.txt`);
	}, [events, offsetGetter, duration, assessmentName, studentName]);

	const handleDownloadAudioTranscript = useCallback(() => {
		setShowAudioTranscriptPDF(true);
	}, []);

	const audioTranscriptPortal = showAudioTranscriptPDF ? (
		<div aria-hidden style={{ position: "absolute", left: "-10000px", pointerEvents: "none" }}>
			<AudioTranscriptPDFGenerator
				autoGenerate={true}
				onComplete={() => setShowAudioTranscriptPDF(false)}
				component={
					<AudioTranscriptReport
						courseName={courseName || "Unknown Course"}
						assessmentName={assessmentName || "Unknown Assessment"}
						studentName={studentName || "Unknown Student"}
						attemptNumber={attemptNumber?.toString() || "Unknown Attempt"}
						assessmentStatus={assessmentStatus || "Unknown Status"}
						recordingDate={recordingDate || "Unknown Date"}
						transcripts={transcripts}
					/>
				}
			/>
		</div>
	) : null;

	return {
		transcripts,
		isDownloadKeystrokesAvailable,
		isDownloadAppActivityAvailable,
		isDownloadAudioTranscriptAvailable,
		handleDownloadKeystrokes,
		handleDownloadApplicationActivity,
		handleDownloadAudioTranscript,
		/**
		 * True while the transcript PDF is being rendered and saved. The keystroke and app-activity
		 * downloads have no equivalent: they serialize data that's already in memory and hand it to the
		 * browser synchronously, so there is no in-between state to report.
		 */
		isAudioTranscriptDownloading: showAudioTranscriptPDF,
		audioTranscriptPortal,
	};
}
